import { SwapOutlined } from "@ant-design/icons";
import { Space, Tooltip } from "antd";
import ErrorBoundary from "components/error-boundary";
import CriticalActivityIndicator from "components/links/critical-activity-indicator";
import ActivityIcon from "components/svg-icons/activity-icon";
import { TLinkSource } from "components/feature-entity-linking-page/models";
import { useState } from "react";
import { DateUtils } from "utils/dateutils";
import AIPoweredIcon from "components/svg-icons/ai-powered-icon";
import ActivityCardSetting from "./activity-card-setting";
import OffsetDurationInputBox from "./offset-duration-input-box";
import DeletedActivityTag from "components/deleted-activity-tag";

function FeatureRowActivityCard({
  item,
  isEditing,
  onRemoveRow,
  isAllowSetting,
  onOffsetChange,
  updateReverseLinking
}: {
  item: TLinkSource;
  isEditing: boolean;
  onRemoveRow: (rowId: string) => void;
  isAllowSetting: boolean;
  onOffsetChange: (prop: {
    offset: number | null;
    activityLink: TLinkSource;
  }) => void;
  updateReverseLinking: (prop: { activityLink: TLinkSource }) => void;
}) {
  const [open, setOpen] = useState(false);

  const task = item.feature_instance;
  if (!task) return null;

  const taskData = task?.feature_activities![0];

  const isPermissionEnabled = taskData?.total_slack_computed !== "restricted";

  const canEdit = true; // Assuming canEdit is always true for this example

  const isEditingActivity = isEditing && !task.deleted_from_source;

  return (
    <div className="flex border border-color bg-white rounded overflow-hidden mb-1">
      {item.recommended_by_system && (
        <div
          className="flex flex-col w-7 items-center justify-center rounded-l bg-ciq-golden-purple-100 border-0 border-r border-solid border-ciq-golden-purple-400"
          data-testid="ai-powered-indicator"
        >
          <AIPoweredIcon />
        </div>
      )}
      <div className="flex flex-col justify-center p-2 features-card w-full">
        {/* Same shape as the Submittal/Material tiles: icon, then one
            `flex-grow truncate` child inside the Tooltip, then the right-hand
            group. antd v6 no longer forwards the Tooltip's own className onto
            the wrapper it generates around multiple children, so that wrapper
            has to be explicit — without it the row loses its flex layout. */}
        <div className="flex items-center pr-1">
          {isPermissionEnabled && taskData?.critical ? (
            <div className="ml-[2px]">
              <CriticalActivityIndicator />
            </div>
          ) : (
            <ActivityIcon
              size={20}
              fill="var(--ciq-gray-500)"
              className="grow-none"
              data-testid="activity-icon"
            />
          )}

          {task.deleted_from_source && (
            <div className="h-fit w-fit items-center justify-center px-1.5">
              <DeletedActivityTag />
            </div>
          )}

          <Tooltip
            title={task.title}
            placement="topLeft"
            data-testid="task-title-tooltip"
          >
            <div className="flex-grow truncate">
              {/* mr-2 mirrors the gap the Submittal/Material tiles get from
                  their spec-section element, which activities don't have. */}
              <i className="mr-2 entity-id-with-spec" data-testid="task-number">
                {task?.number}
              </i>
              {item.governing_link ? (
                <span
                  className="text-base mr-1 pt-[10px] leading-[0]"
                  data-testid="governing-link-indicator"
                >
                  *
                </span>
              ) : (
                ""
              )}
              <span data-testid="task-title">{task.title}</span>
            </div>
          </Tooltip>
          {/* Right-hand group — the date sits here rather than inside the
              Tooltip child so a long title truncates instead of pushing it
              out of the row. */}
          <div className="flex items-center space-x-2 shrink-0">
            {taskData?.start_date && item.isLinkedWithStartdate && (
              <i className="entity-id-with-spec" data-testid="start-date">
                Start Date :{" "}
                {taskData?.start_date
                  ? DateUtils.format(taskData?.start_date)
                  : // outer guard (taskData?.start_date && ...) already ensures start_date is truthy here
                    /* v8 ignore next */
                    ""}
              </i>
            )}
            {taskData?.end_date && !item.isLinkedWithStartdate && (
              <i className="entity-id-with-spec" data-testid="end-date">
                End Date :{" "}
                {taskData?.end_date
                  ? DateUtils.format(taskData?.end_date)
                  : // outer guard (taskData?.end_date && ...) already ensures end_date is truthy here
                    /* v8 ignore next */
                    ""}
              </i>
            )}
            {isEditingActivity && (
              <div className="flex justify-between items-center">
                <Space
                  size="small"
                  onClick={(e: any) => {
                    e.stopPropagation();
                    onRemoveRow(task.id);
                  }}
                  className="unlink-button grow-none"
                  data-testid="unlink-button"
                >
                  Unlink
                </Space>
                {isAllowSetting && (
                  <ActivityCardSetting
                    activityLinkedItem={item}
                    openSettings={open}
                    setOpenSettings={setOpen}
                    onOffsetChange={onOffsetChange}
                    updateReverseLinking={updateReverseLinking}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        {item.reverse_dependency && (
          <div className="flex items-center space-x-2 py-1 pt-2 mt-2">
            {item.reverse_dependency && (
              <div className="flex " data-testid="predecessor-indicator">
                <Space
                  className="px-4 py-1 flex bg-ciq-gray-100 rounded-sm h-[32px]"
                  data-testid="predecessor-badge"
                >
                  <SwapOutlined
                    className="text-ciq-gray-700"
                    data-testid="swap-icon"
                  />{" "}
                  Activity is the Predecessor
                </Space>
              </div>
            )}
          </div>
        )}
        {(item.offset_duration || open) && (
          <ErrorBoundary>
            <div className="flex py-1">
              <OffsetDurationInputBox
                task={item}
                onOffsetChange={(params) => {
                  setOpen(false);
                  onOffsetChange(params);
                }}
                canEdit={canEdit && isEditingActivity}
                openToEdit={open && isEditingActivity}
                data-testid="offset-duration-input"
              />
            </div>
          </ErrorBoundary>
        )}
      </div>
    </div>
  );
}

export default FeatureRowActivityCard;
